// apps/client/src/state/slices/core.ts
import type { StringKey, StringParams } from '@whiskerton/content';
import type { TownId } from '@whiskerton/shared';
import type { ScreenId, ScreenParams } from '../../ui/router';
import type { SliceCreator } from '../types';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends CoreSlice {} }

export type Phase = 'boot' | 'signedOut' | 'needsProfile' | 'towns' | 'connecting' | 'inTown' | 'updateRequired';
export interface ClientSettings { music: number; sfx: number; haptics: boolean; quality: 'auto' | 'low' | 'high'; perfOverlay: boolean }
/** `at` = when it was shown or last refreshed (Date.now()); it stays up for TOAST_MS after that (ui/kit/Toast.tsx). */
export interface Toast { id: number; key: StringKey; params?: StringParams; tone: 'info' | 'warn'; at?: number }
export interface CoreSlice {
  phase: Phase; setPhase(p: Phase): void;
  enterTownRequest: TownId | null;
  requestEnterTown(id: TownId): void; clearEnterTownRequest(): void;
  screens: { id: ScreenId; params: unknown }[];
  openScreen<K extends ScreenId>(id: K, params: ScreenParams[K]): void; closeScreen(): void; closeAll(): void;
  toasts: Toast[]; toast(key: StringKey, params?: StringParams, tone?: Toast['tone']): void; dismissToast(id: number): void;
  /** Why you are back on the towns screen when a toast is too brief for it (v1 round 3: removed from a town); shown there until you
   *  enter a town or dismiss it. */
  townsNotice: { key: StringKey; params?: StringParams } | null; setTownsNotice(n: { key: StringKey; params?: StringParams } | null): void;
  banner: 'reconnecting' | null;
  settings: ClientSettings; setSettings(p: Partial<ClientSettings>): void;
}

const SETTINGS_KEY = 'whiskerton-settings';
export const DEFAULT_SETTINGS: ClientSettings = { music: 0.7, sfx: 0.8, haptics: true, quality: 'auto', perfOverlay: false };
const MAX_TOASTS = 3;
function loadSettings(): ClientSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<ClientSettings>) } : DEFAULT_SETTINGS;
  } catch { return DEFAULT_SETTINGS; }
}
let toastSeq = 0;
const sameParams = (a: StringParams | undefined, b: StringParams | undefined): boolean => {
  const ka = Object.keys(a ?? {}), kb = Object.keys(b ?? {});
  return ka.length === kb.length && ka.every((k) => a![k] === b?.[k]);
};

export const slice: SliceCreator<CoreSlice> = (set, get) => ({
  phase: 'boot',
  setPhase: (phase) => set({ phase }),
  enterTownRequest: null,
  requestEnterTown: (id) => set({ enterTownRequest: id }),
  clearEnterTownRequest: () => set({ enterTownRequest: null }),
  screens: [],
  openScreen: (id, params) => set((s) => ({ screens: [...s.screens, { id, params }] })),
  closeScreen: () => set((s) => ({ screens: s.screens.slice(0, -1) })),
  closeAll: () => set({ screens: [] }),
  toasts: [],
  // A toast identical to one already showing refreshes that one (same place, same id, timer restarted) instead of stacking a copy
  // (v1 round 4: a repeated 'Get a little closer.' showed twice).
  toast: (key, params, tone = 'info') => set((s) => {
    const at = Date.now(), same = s.toasts.find((x) => x.key === key && x.tone === tone && sameParams(x.params, params));
    if (same) return { toasts: s.toasts.map((x) => (x === same ? { ...x, at } : x)) };
    return { toasts: [...s.toasts, { id: ++toastSeq, key, params, tone, at }].slice(-MAX_TOASTS) };
  }),
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  townsNotice: null,
  setTownsNotice: (townsNotice) => set({ townsNotice }),
  banner: null,
  settings: loadSettings(),
  setSettings: (p) => {
    const settings = { ...get().settings, ...p };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* private mode / no storage */ }
    set({ settings });
  },
});
